import { useEffect, useState } from 'react'
import { WifiOff } from 'lucide-react'
import { offlineCaptureAvailable } from '@/lib/offline/capability'
import { readPool } from '@/lib/offline/orderNumberPool'
import { desktop } from '@/lib/printTransport'
import { useOnlineAndReachable } from '@/lib/offline/reachability'

/**
 * The offline banner.
 *
 * Its wording has to track what the app can ACTUALLY do. Before F-FE-N30 it
 * said new orders needed a connection, which was true. Now a prepared desktop
 * terminal can take sales offline, so saying that would be a lie — and a banner
 * that under-promises is as bad as one that over-promises: staff stop taking
 * sales they could have taken.
 *
 * So there are two messages, chosen by the same predicate that gates the
 * button. A terminal that is NOT prepared keeps the old, honest wording.
 */
export function OfflineIndicator() {
  // F-FE-N30: the banner must agree with the button. Both key on backend
  // reachability, not just navigator.onLine.
  const { online: osOnline, backendReachable } = useOnlineAndReachable()
  const online = osOnline && backendReachable
  const [canCapture, setCanCapture] = useState(false)

  useEffect(() => {
    if (online) return
    let cancelled = false
    void (async () => {
      const pool = await readPool()
      const cap = offlineCaptureAvailable({
        online: osOnline,
        backendReachable,
        isDesktop: desktop() != null,
        reservedNumbers: pool?.numbers.length ?? 0,
      })
      if (!cancelled) setCanCapture(cap.ok)
    })()
    return () => {
      cancelled = true
    }
  }, [online, osOnline, backendReachable])

  if (online) return null

  return (
    <div className="fixed bottom-3 left-1/2 z-[100] -translate-x-1/2">
      <div className="flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-900 shadow-lg dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
        <WifiOff className="h-4 w-4 shrink-0" />
        <span>
          {canCapture
            ? 'Offline — sales are held on this device. Payment resumes when you reconnect.'
            : 'Offline — showing the last saved catalogue. New sales and payments need a connection.'}
        </span>
      </div>
    </div>
  )
}
